import * as React from 'react';

import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';

import { useLoaderData, useNavigate } from 'react-router-dom';

import { useForm, FormProvider } from 'react-hook-form';
import Form from '../components/form/Form';
import FormDropDown from '../components/form/FormDropDown';
import FormButton from '../components/form/FormButton';
import FormObjectArrayList from '../components/form/FormObjectArrayList';

import Header from '../components/Header';
import GoBack from '../components/GoBack';

import { newBehaviorTagSet, getBehaviorTagSet, saveBehaviorTagSet } from '../models/behaviorTags';
import { useAuth } from '../context/AuthProvider';
import { useMessage } from '../context/MessageProvider';
import { useConnectivity } from '../context/ConnectivityProvider';

import { schema } from '../models/biologySchema';
import { behaviorsSource } from './AssessmentSectionBFormPage';

export async function loader({ params }) {
  const behaviorTagSet = params?.behaviorTagSetId
    ? await getBehaviorTagSet(params.behaviorTagSetId)
    : newBehaviorTagSet();
  return { behaviorTagSet: behaviorTagSet || newBehaviorTagSet() };
}

const resolveSpeciesRow = (speciesValue) => {
  if (!speciesValue) {
    return { phylum: '', classes: '', order: '', genus: '', species: '' };
  }

  for (const phylum of schema) {
    for (const classes of phylum.class || []) {
      for (const order of classes.order || []) {
        for (const genus of order.genus || []) {
          for (const species of genus.species || []) {
            if (species.scientificName === speciesValue) {
              return {
                phylum: phylum.scientificName,
                classes: classes.scientificName,
                order: order.scientificName,
                genus: genus.scientificName,
                species: speciesValue,
              };
            }
          }
        }
      }
    }
  }

  return { phylum: '', classes: '', order: '', genus: '', species: speciesValue };
};

const toLabelOption = (cur) => ({
  value: cur.scientificName,
  label: `${cur.commonName} (${cur.scientificName})`,
});

export default function BehaviorTagSetFormPage({ title = 'Behavior Tags', ...props }) {
  const navigate = useNavigate();
  const { behaviorTagSet } = useLoaderData();
  const { facilityId } = useAuth();
  const { isOnline } = useConnectivity();
  const { setSuccess, setError } = useMessage();
  const [working, setWorking] = React.useState(false);

  const defaultValues = {
    ...behaviorTagSet,
    species_row: resolveSpeciesRow(behaviorTagSet.species),
    tags: Array.isArray(behaviorTagSet.tags) ? behaviorTagSet.tags : [],
  };
  const methods = useForm({ defaultValues });

  const phylumOptions = schema.map((cur) => toLabelOption(cur));

  const getClassesList = (phylumValue) =>
    schema.find((cur) => cur.scientificName === phylumValue)?.class || [];
  const getOrderList = (classesList, classesValue) =>
    classesList.find((cur) => cur.scientificName === classesValue)?.order || [];
  const getGenusList = (orderList, orderValue) =>
    orderList.find((cur) => cur.scientificName === orderValue)?.genus || [];
  const getSpeciesList = (genusList, genusValue) =>
    genusList.find((cur) => cur.scientificName === genusValue)?.species || [];

  const onSubmit = async (data) => {
    try {
      setWorking(true);

      const payload = { ...behaviorTagSet, ...data };
      payload.species = data.species_row?.species || '';
      delete payload.species_row;

      await saveBehaviorTagSet(payload, facilityId, isOnline);
      setSuccess('Behavior Tags Saved');
      navigate('/behavior-tags');
    } catch (error) {
      console.error(error);
      setError(error);
    } finally {
      setWorking(false);
    }
  };

  const speciesRow = methods.watch('species_row') || {};
  const behaviorOptions = behaviorsSource.map((behavior) => ({
    value: behavior.behaviorKey,
    label: behavior.title,
  }));

  const classesOptions = getClassesList(speciesRow.phylum || '').map((cur) => toLabelOption(cur));
  const orderOptions = getOrderList(getClassesList(speciesRow.phylum || ''), speciesRow.classes || '').map((cur) =>
    toLabelOption(cur)
  );
  const genusOptions = getGenusList(
    getOrderList(getClassesList(speciesRow.phylum || ''), speciesRow.classes || ''),
    speciesRow.order || ''
  ).map((cur) => toLabelOption(cur));
  const speciesOptions = getSpeciesList(
    getGenusList(
      getOrderList(getClassesList(speciesRow.phylum || ''), speciesRow.classes || ''),
      speciesRow.order || ''
    ),
    speciesRow.genus || ''
  ).map((cur) => toLabelOption(cur));

  return (
    <Container component="main" maxWidth="sm">
      <GoBack onClick={() => navigate('/behavior-tags')} />
      <Header label="Edit Specific Behavior Tags" />

      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid xs={12}>
              <FormDropDown name="behavior_key" label="Behavior" list={behaviorOptions} required />
            </Grid>

            <Grid xs={12}>
              <Typography variant="subtitle1">Species</Typography>
            </Grid>

            <Grid xs={12}>
              <FormDropDown
                name="species_row.phylum"
                label="Phylum"
                list={phylumOptions}
                required
              />
            </Grid>
            <Grid xs={12}>
              <FormDropDown
                name="species_row.classes"
                label="Class"
                list={classesOptions}
                required
                disabled={!speciesRow.phylum}
              />
            </Grid>
            <Grid xs={12}>
              <FormDropDown
                name="species_row.order"
                label="Order"
                list={orderOptions}
                required
                disabled={!speciesRow.classes}
              />
            </Grid>
            <Grid xs={12}>
              <FormDropDown
                name="species_row.genus"
                label="Genus"
                list={genusOptions}
                required
                disabled={!speciesRow.order}
              />
            </Grid>
            <Grid xs={12}>
              <FormDropDown
                name="species_row.species"
                label="Species"
                list={speciesOptions}
                required
                disabled={!speciesRow.genus}
              />
            </Grid>

            <Grid xs={12}>
              <Typography variant="subtitle1">Specific Behavior Tags</Typography>
            </Grid>

            <Grid xs={12}>
              <FormObjectArrayList
                name="tags"
                label="Tags"
                showRowIndex={false}
                buttonText="Add Tag"
                emptyText="No tags defined."
                newRow={() => ({ label: '', description: '' })}
                fields={[
                  {
                    key: 'label',
                    label: 'Label',
                    component: TextField,
                    componentProps: { required: true },
                    gridProps: { xs: 12, sm: 6 },
                  },
                  {
                    key: 'description',
                    label: 'Description',
                    component: TextField,
                    gridProps: { xs: 12, sm: 6 },
                  },
                ]}
              />
            </Grid>

            <Grid xs={12}>
              <FormButton onSubmit={methods.handleSubmit(onSubmit)} fullWidth working={working}>
                Save
              </FormButton>
            </Grid>
          </Grid>
        </Form>
      </FormProvider>
    </Container>
  );
}
